import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const NotificationContext = createContext({ connected: false, subscribe: () => () => {}, notifications: [] });

const MAX_STORED = 30;

const PUSH_CONFIG = {
  new_assignment: { title: 'New Assignment Available', icon: '/favicon.ico' },
  assignment_taken: { title: 'Assignment Accepted', icon: '/favicon.ico' },
  assignment_cancelled: { title: 'Assignment Cancelled', icon: '/favicon.ico' },
};

function requestPushPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

function showBrowserNotification(event) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const config = PUSH_CONFIG[event.type];
  if (!config) return;
  let body = '';
  if (event.data?.target_language) body += `Language: ${event.data.target_language.toUpperCase()}`;
  if (event.data?.duration_minutes) body += ` · ${event.data.duration_minutes}min`;
  if (event.data?.accepted_by) body += `\nBy: ${event.data.accepted_by}`;
  try {
    new Notification(config.title, { body: body || 'Check the app for details', icon: config.icon, tag: event.type });
  } catch (e) { /* ignore — some browsers block from workers */ }
}

export function NotificationProvider({ children }) {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const retryCount = useRef(0);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const subscribersRef = useRef(new Set());

  const dispatch = useCallback((event) => {
    // Add to stored notifications
    setNotifications(prev => [{
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      ...event,
      read: false,
    }, ...prev].slice(0, MAX_STORED));
    // Show browser push notification if tab is in background
    if (document.hidden) showBrowserNotification(event);
    // Notify all subscribers
    subscribersRef.current.forEach(fn => {
      try { fn(event); } catch (e) { /* ignore */ }
    });
  }, []);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Retry in 2s — user may not be logged in yet
      reconnectTimer.current = setTimeout(connect, 2000);
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const host = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${host}/api/ws/notifications?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => { setConnected(true); retryCount.current = 0; requestPushPermission(); };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'pong') return;
          dispatch(data);
        } catch (e) { /* ignore */ }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        const delay = Math.min(1000 * Math.pow(2, retryCount.current), 30000);
        retryCount.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };

      ws.onerror = () => { ws.close(); };
    } catch (e) {
      const delay = Math.min(1000 * Math.pow(2, retryCount.current), 30000);
      retryCount.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    }
  }, [dispatch]);

  useEffect(() => {
    connect();
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send('ping');
    }, 30000);
    // Reconnect when token changes (login/logout)
    const storageHandler = (e) => {
      if (e.key === 'token') {
        if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
        clearTimeout(reconnectTimer.current);
        retryCount.current = 0;
        if (e.newValue) setTimeout(connect, 500);
        else setConnected(false);
      }
    };
    window.addEventListener('storage', storageHandler);
    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer.current);
      window.removeEventListener('storage', storageHandler);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [connect]);

  const subscribe = useCallback((fn) => {
    subscribersRef.current.add(fn);
    return () => subscribersRef.current.delete(fn);
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => { setNotifications([]); }, []);

  return (
    <NotificationContext.Provider value={{ connected, subscribe, notifications, markAllRead, clearAll }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  return useContext(NotificationContext);
}

/**
 * Subscribe to specific notification events. Returns {connected}.
 * This replaces the old useNotifications hook for pages that need auto-refresh.
 */
export function useNotificationEvents(onEvent) {
  const { connected, subscribe } = useNotificationContext();
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    return subscribe((event) => {
      if (onEventRef.current) onEventRef.current(event);
    });
  }, [subscribe]);

  return { connected };
}
